import { Grid } from '@mui/material';
import { RoutalDialog, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import gorillaImage from '../assets/img/subscription/gorilla-icon.svg';
import kongImage from '../assets/img/subscription/kong-icon.svg';
import monkeyImage from '../assets/img/subscription/monkey-icon.svg';
import { PlanType } from '../domain/subscription/Subscription';
import { useUpdateSubscriptionPlan } from '../hooks/backoffice/subscriptions/useUpdateSubscriptionPlan';
import { useRetrieveUser } from '../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../hooks/user/useRetrieveUserCredentials';
import Button from '../react-components/Button';
import InputText from '../react-components/InputText';
import Text from '../react-components/Text';
import { InputDatePicker } from '../react-components/input-date-picker/InputDatePicker';

const PlanSelectorImg = styled.img<{ selected: boolean }>`
  ${(props) =>
    props.selected
      ? `
    cursor: default;
    opacity: 1;
  `
      : `
    cursor: pointer;
    opacity: 0.3;
  `}
  &:hover {
    opacity: 1;
  }
`;

interface SubscriptionData {
  extra_vehicles: number;
  extra_supervisors: number;
  extra_projects: number;
  extra_users: number;
  plan: PlanType;
  current_period_end: Date;
}

interface UpdateSubscriptionDialogProps {
  userId: string;
  subscriptionData: SubscriptionData;
  onClose: (modified?: boolean) => void;
}

export const UpdateSubscriptionDialog = ({ userId, subscriptionData, onClose }: UpdateSubscriptionDialogProps) => {
  const { data: user } = useRetrieveUser();
  const { run: refetchCredentials } = useRetrieveUserCredentials();
  const { run: changePlan, isLoading, success: changePlanSuccess } = useUpdateSubscriptionPlan();
  const [planData, setPlanData] = useState<SubscriptionData>({
    plan: subscriptionData.plan,
    extra_vehicles: subscriptionData.extra_vehicles,
    extra_supervisors: subscriptionData.extra_supervisors,
    extra_projects: subscriptionData.extra_projects,
    extra_users: subscriptionData.extra_users,
    current_period_end: new Date(subscriptionData.current_period_end),
  });

  useEffect(() => {
    setPlanData({
      plan: subscriptionData.plan,
      extra_vehicles: subscriptionData.extra_vehicles,
      extra_supervisors: subscriptionData.extra_supervisors,
      extra_projects: subscriptionData.extra_projects,
      extra_users: subscriptionData.extra_users,
      current_period_end: new Date(subscriptionData.current_period_end),
    });
  }, [subscriptionData]);

  useEffect(() => {
    if (changePlanSuccess) {
      refetchCredentials();
      onClose(true);
    }
  }, [changePlanSuccess, onClose, refetchCredentials]);

  const CHANGE_PLAN_TYPES = [
    {
      id: `monkey` as PlanType,
      img: monkeyImage,
    },
    {
      id: `gorilla` as PlanType,
      img: gorillaImage,
    },
    {
      id: `kong` as PlanType,
      img: kongImage,
    },
  ];

  const isFormDisabled = () => {
    return (
      isLoading ||
      (subscriptionData.plan === planData.plan &&
        subscriptionData.extra_supervisors === planData.extra_supervisors &&
        subscriptionData.extra_projects === planData.extra_projects &&
        subscriptionData.extra_users === planData.extra_users &&
        new Date(subscriptionData.current_period_end).getTime() === planData.current_period_end.getTime())
    );
  };

  return (
    <RoutalDialog
      open
      onCloseDialog={() => onClose()}
      id="update-subscription-dialog"
      titleContent={<Text variant="h6" intlId={`user_profile_summary.change_plan`} />}
      dialogProps={{
        fullWidth: true,
        maxWidth: `sm`,
      }}
      footerContent={
        <>
          <Button variant="flat" intlId="cancel" onClick={() => onClose()} />
          <Button
            intlId="save"
            onClick={() => {
              changePlan({
                userId,
                plan: planData.plan,
                extra_vehicles: planData.extra_vehicles,
                extra_supervisors: planData.extra_supervisors,
                extra_projects: planData.extra_projects,
                extra_users: planData.extra_users,
                current_period_end: planData.current_period_end,
              });
            }}
            disabled={isFormDisabled()}
          />
        </>
      }
    >
      <Grid container style={{ padding: `16px` }} direction="column">
        <Grid item container direction="row" justifyContent="space-around" style={{ padding: `8px` }}>
          {CHANGE_PLAN_TYPES.map((plan, index) => {
            return (
              <PlanSelectorImg
                onClick={(event: any) => {
                  event.stopPropagation();
                  if (planData.plan !== plan.id) {
                    setPlanData({ ...planData, plan: plan.id });
                  }
                }}
                selected={planData.plan === plan.id}
                width="100"
                src={plan.img}
                alt={`${plan.id}_plan`}
                draggable="false"
              />
            );
          })}
        </Grid>

        <VSpacer small />

        <Grid item container direction="column">
          <Text variant="label" intlId="user_profile_summary.change_plan.extra_vehicles" />

          <VSpacer small />

          <InputText
            type="number"
            inputContainerStyles={{ width: `100%` }}
            placeholderIntlId="user_profile_summary.change_plan.extra_vehicles"
            fieldValue={planData.extra_vehicles}
            onChange={(value: any) => {
              setPlanData({ ...planData, extra_vehicles: value });
            }}
          />
        </Grid>

        <VSpacer medium />

        <Grid item container direction="column">
          <Text variant="label" intlId="user_profile_summary.change_plan.extra_supervisors" />

          <VSpacer small />

          <InputText
            type="number"
            inputContainerStyles={{ width: `100%` }}
            placeholderIntlId="user_profile_summary.change_plan.extra_supervisors"
            fieldValue={planData.extra_supervisors}
            onChange={(value: any) => {
              setPlanData({ ...planData, extra_supervisors: value });
            }}
          />
        </Grid>

        <VSpacer medium />

        <Grid item container direction="column">
          <Text variant="label" intlId="user_profile_summary.change_plan.extra_projects" />

          <VSpacer small />

          <InputText
            type="number"
            inputContainerStyles={{ width: `100%` }}
            placeholderIntlId="user_profile_summary.change_plan.extra_projects"
            fieldValue={planData.extra_projects}
            onChange={(value: any) => {
              setPlanData({ ...planData, extra_projects: value });
            }}
          />
        </Grid>

        <VSpacer medium />

        <Grid item container direction="column">
          <Text variant="label" intlId="user_profile_summary.change_plan.extra_users" />

          <VSpacer small />

          <InputText
            type="number"
            inputContainerStyles={{ width: `100%` }}
            placeholderIntlId="user_profile_summary.change_plan.extra_users"
            fieldValue={planData.extra_users}
            onChange={(value: any) => {
              setPlanData({ ...planData, extra_users: value });
            }}
          />
        </Grid>

        <VSpacer medium />

        <Grid item container direction="column">
          <Text variant="label" intlId="user_profile_summary.change_plan.current_period_end" />

          <VSpacer small />

          <InputDatePicker
            dateValue={planData.current_period_end}
            onChange={(date: Date) => {
              setPlanData({ ...planData, current_period_end: date });
            }}
            locale={user?.language.value}
            portalId="update-subscription-dialog"
          />
        </Grid>
      </Grid>
    </RoutalDialog>
  );
};
